import React, { useState, useMemo, useRef, useEffect } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import store from "../data/store/store.js";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { IonIcon } from "@ionic/react";
import { chevronDown, chevronForward } from "ionicons/icons";
import processScript from "./itemGenerator/processScript.js";
import ObjectViewer from "../model/ObjectViewer.js";
import { clearSelectedScriptItems } from "../data/store/actions/scriptStoreFunctions.js";
import GeneratedItemsList from "../modules/helpers/GeneratedItemsList.js";
import { addDraftCreatedItem } from "../data/store/actions/objectStoreFunctions.js";
import {
  getAllScripts,
  getSelectedClient,
  getSelectedWork,
  getAllSettings,
  getScriptItemsByScript
} from "../data/getters.js";
import {
  Engine,
  Scene,
  Vector3,
  MeshBuilder,
  ArcRotateCamera,
  HemisphericLight,
  StandardMaterial,
  Color3,
  Color4
} from "@babylonjs/core";
import scriptItemApi from "../data/api/scriptItemApi.js";
import { addDraftObject } from "../data/store/actions/objectStoreFunctions.js";
import scriptSaveApi from "../data/api/scriptSaveApi.js";
import { useLanguage } from "../modules/helpers/LanguageContext.js"; // ellenőrizd az elérési utat
import { nextDraftId } from "./draftIdCounter.js";
import {
  createUid,
  getDynamicRowFixedKey,
  getDynamicRowValueKey,
  normalizeVec,
  parseBracketList,
  parseDynamicValueListConfig,
  parseRefMods,
  parseRepeatConfig,
  parseSetting,
  parseValueListPairs,
  resolveText
} from "./scriptCallerUtil.js";
import "./ScriptCaller.css";
// és a komponens elején az első useState fölé:
export default function ScriptCaller({
  initialObject,
  initialScriptId,
  initialConfig,
  initialDims,
  initialGeneratedItems = [],
  onSave,
  palette,
  openPaletteModal,
  closePaletteModal,
  onColorSelect
}) {
  console.log("ScriptCaller beállítások:", {
    initialScriptId,
    initialConfig,
    initialDims,
    initialGeneratedItems
  });
  const { t } = useLanguage();
  const dispatch = useDispatch();
  // selectors via dispatch(useSelector)
  const scripts = dispatch(useSelector(getAllScripts, shallowEqual)) || [];
  const allSettings = dispatch(useSelector(getAllSettings, shallowEqual)) || [];
  const selectedWorkFromStore = dispatch(
    useSelector(getSelectedWork, shallowEqual)
  );
  const selectedClientFromStore = dispatch(
    useSelector(getSelectedClient, shallowEqual)
  );
  const scriptSaves = useSelector((state) => state.scriptSaves) || [];
  const [dims, setDims] = useState({ width: "", height: "", depth: "" });
  const [hoveredScript, setHoveredScript] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState("");
  const [config, setConfig] = useState({});
  const [generatedItems, setGeneratedItems] = useState([]);
  const [objectToView, setObjectToView] = useState(null);
  const [objectToViewData, setObjectToViewData] = useState(null);
  const [collapsedColors, setCollapsedColors] = useState({});
  const [defsCache, setDefsCache] = useState(new Map());
  // state-ek közé
  const generatedItemsRef = useRef([]);
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [swapSourceColor, setSwapSourceColor] = useState(undefined);
  const [collapsedBox, setCollapsedBox] = useState({});
  const toggleBox = (key) =>
    setCollapsedBox((prev) => ({ ...prev, [key]: !prev[key] }));
  useEffect(() => {
    setCollapsedColors((prev) => {
      const next = { ...prev };
      palette.forEach((c) => {
        const cid = c.colorId;
        if (next[cid] === undefined) {
          next[cid] = false; // új színek default nyitva
        }
      });
      Object.keys(next).forEach((key) => {
        if (!palette.some((c) => c.colorId === Number(key))) delete next[key];
      });
      return next;
    });
  }, [palette]);

  useEffect(() => {
    generatedItemsRef.current = generatedItems;
  }, [generatedItems]);
  function getDynamicRowCount(raw, cfg) {
    const dyn = parseDynamicValueListConfig(raw);
    if (!dyn?.countId) return 0;

    const countKey = dyn.countSection
      ? `${dyn.countId}@${dyn.countSection}`
      : dyn.countId;

    return parseInt(cfg[countKey] ?? 0, 10) || 0;
  }
  const toggleColor = (cid) =>
    setCollapsedColors((prev) => ({ ...prev, [cid]: !prev[cid] }));
  const [hoverMeta, setHoverMeta] = useState({
    id: null,
    distance: 0,
    duration: 10
  });
  const [hiddenItems, setHiddenItems] = useState(new Set());
  const handleToggleVisibility = (key) => {
    setHiddenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const savedScriptsForRoom = useMemo(() => {
    if (!selectedRoom) return [];

    return scriptSaves
      .map((save) => {
        const script = scripts.find((s) => s.scriptId === save.scriptId);
        if (!script) return null;

        if (script.room !== selectedRoom && script.room !== "All") return null;

        return {
          ...script,
          saveId: save.saveId,
          saveName: save.name,
          savedConfig: save.setting ? JSON.parse(save.setting) : {},
          __isSaved: true
        };
      })
      .filter(Boolean);
  }, [scriptSaves, scripts, selectedRoom]);
  const normalScriptsForRoom = useMemo(() => {
    if (!selectedRoom) return [];

    return scripts.map((s) => ({ ...s, __isSaved: false }));
  }, [scripts, selectedRoom]);
  const scriptsForRoom = useMemo(() => {
    return [...savedScriptsForRoom, ...normalScriptsForRoom];
  }, [savedScriptsForRoom, normalScriptsForRoom]);
  const handleCardEnter = (id) => (e) => {
    const nameEl = e.currentTarget.querySelector("[data-script-name]");
    if (nameEl) {
      const textW = nameEl.scrollWidth || 0; // teljes szövegszélesség
      const viewW = nameEl.clientWidth || 0; // látható szélesség
      const overflow = textW - viewW > 1;
      const GAP = 60; // kis „szusszanás”
      const moveDistance = overflow ? textW + GAP : 0;

      const SPEED = 80; // px/s (kicsit gyorsabb a korábbinál)
      const duration = moveDistance
        ? Math.min(18, Math.max(6, moveDistance / SPEED))
        : 0;

      setHoverMeta({
        id,
        distance: moveDistance, // ezt használjuk CSS var-ként
        duration,
        gap: GAP,
        overflow
      });
    } else {
      setHoverMeta({ id, distance: 0, duration: 0, gap: 0, overflow: false });
    }
    setHoveredScript(id);
  };

  const handleCardLeave = () => setHoveredScript(null);
  // map settingId → name
  const settingNameById = useMemo(() => {
    const m = {};
    allSettings.forEach((s) => {
      m[s.settingId] = t(s.name);
    });
    return m;
  }, [allSettings]);

  // (*17*) vagy (*71@2*) makrók + width/height/depth behelyettesítés, majd egyszerű aritmetika kiértékelése
  const macroRe = /\(\*(\d+)(?:@(\d+))?\*\)/g;

  function syncRepeaterRows(next, changedKey = null, dimsObj = dims) {
    (settingsMeta || []).forEach((meta) => {
      const id = String(meta.settingId);

      const hint = parseRepeatConfig(next[id]);
      const repSource = meta?.repeatSource ?? hint?.source ?? null;
      const repCountFrom = meta?.repeatCountFrom ?? hint?.countId ?? null;

      if (!repSource) return;
      if (changedKey && repCountFrom !== changedKey) return;

      const sourceMeta = getSettingById(settingsMeta, repSource);

      const rawOptions = sourceMeta?.valuePairs?.length
        ? sourceMeta.valuePairs
        : (sourceMeta?.valueList || []).map((v) => ({ label: v, value: v }));

      const options = rawOptions.map((opt) => ({
        label: resolveText(opt.label, next, dimsObj, t),
        value: resolveText(opt.value, next, dimsObj, t)
      }));

      const N = parseInt(next[repCountFrom] ?? 0, 10) || 0;
      const defVal = sourceMeta?.defaultValue || options[0]?.value || "";

      for (let i = 1; i <= N; i++) {
        const k = `${id}@${i}`;
        if (next[k] == null) next[k] = defVal;
      }

      Object.keys(next)
        .filter((k) => k.startsWith(`${id}@`))
        .map((k) => parseInt(k.split("@")[1], 10))
        .filter(Number.isFinite)
        .forEach((ix) => {
          if (ix > N) delete next[`${id}@${ix}`];
        });
    });

    return next;
  }
  useEffect(() => {
    if (!selectedScript) return;

    setConfig((prev) =>
      finalizeConfig(
        {
          ...prev,
          width: dims.width,
          height: dims.height,
          depth: dims.depth
        },
        { dimsOverride: dims }
      )
    );
  }, [dims.width, dims.height, dims.depth, selectedScript]);

  function buildProcessScriptConfig(rawConfig) {
    const next = { ...rawConfig };

    (settingsMeta || []).forEach((meta) => {
      if (!meta?.isDynamicValueList) return;

      const id = String(meta.settingId);

      const raw =
        getRawSettingDefinition(selectedScript, id) ??
        rawConfig[id] ??
        meta.defaultValue ??
        "";

      const rowCount = getDynamicRowCount(raw, rawConfig);
      const values = [];

      for (let i = 1; i <= rowCount; i++) {
        const valueKey = getDynamicRowValueKey(id, i);
        values.push(Number(rawConfig[valueKey] ?? 0) || 0);
      }

      // a root kulcs kapja a lista-stringet
      next[id] = `[${values.join(",")}]`;

      // a segéd kulcsokat kivesszük a processScript inputból
      for (let i = 1; i <= rowCount; i++) {
        delete next[getDynamicRowValueKey(id, i)];
        delete next[getDynamicRowFixedKey(id, i)];
      }
    });

    return next;
  }

  function recomputeDependentValues(baseCfg, dimsObj = dims) {
    const next = { ...baseCfg };

    Object.keys(next).forEach((k) => {
      const v = next[k];
      const meta = getSettingById(settingsMeta, k);

      if (meta?.isDynamicValueList) return;

      if (
        typeof v === "string" &&
        (macroRe.test(v) || /\b(width|height|depth)\b/.test(v))
      ) {
        macroRe.lastIndex = 0;
        next[k] = resolveText(v, next, dimsObj, t);
      }
    });

    (settingsMeta || []).forEach((meta) => {
      const id = String(meta.settingId);
      const cur = next[id];

      if (meta?.isDynamicValueList) return;

      if (
        (cur == null || cur === "") &&
        typeof meta?.defaultValue === "string" &&
        (macroRe.test(meta.defaultValue) ||
          /\b(width|height|depth)\b/.test(meta.defaultValue))
      ) {
        macroRe.lastIndex = 0;
        next[id] = resolveText(meta.defaultValue, next, dimsObj, t);
      }
    });

    return next;
  }

  const allowedSettingIds = useMemo(() => {
    if (!selectedScript?.setting) return [];
    return selectedScript.setting
      .split(",")
      .map((p) => p.split(":")[0].trim())
      .filter(Boolean);
  }, [selectedScript]);
  function toggleDynamicRowFixed(settingId, rowIndex) {
    setConfig((prev) => {
      const next = { ...prev };
      const fixedKey = getDynamicRowFixedKey(settingId, rowIndex);

      next[fixedKey] = !(next[fixedKey] === true);

      return finalizeConfig(next, { changedKey: fixedKey });
    });
  }

  function setDynamicRowManualValue(settingId, rowIndex, value) {
    setConfig((prev) => {
      const next = { ...prev };
      const valueKey = getDynamicRowValueKey(settingId, rowIndex);
      const fixedKey = getDynamicRowFixedKey(settingId, rowIndex);

      next[fixedKey] = true;
      next[valueKey] = value === "" ? 0 : Number(value);

      return finalizeConfig(next, { changedKey: valueKey });
    });
  }

  function evaluateDynamicFormula(formula, cfg, dimsObj, extra = {}) {
    if (!formula) return 0;

    let expr = String(formula).trim();

    const formatValueForExpr = (raw) => {
      if (raw == null) return "0";

      if (typeof raw === "number") {
        return Number.isFinite(raw) ? String(raw) : "0";
      }

      const s = String(raw).trim();

      if (s === "") return `""`;
      if (/^-?\d+(\.\d+)?$/.test(s)) return s;
      if (s === "true" || s === "false" || s === "null") return s;

      return JSON.stringify(s);
    };

    expr = expr.replace(/\(\*(\d+)(?:@(\d+))?\*\)/g, (_, id, sec) => {
      const key = sec ? `${id}@${sec}` : id;
      return formatValueForExpr(cfg[key] ?? 0);
    });

    expr = expr.replace(/\b(width|height|depth)\b/g, (_, k) =>
      String(cfg[k] ?? dimsObj?.[k] ?? 0)
    );

    expr = expr.replace(/\ball\.len\b/g, String(extra.allLen ?? 0));
    expr = expr.replace(/\bfix\.len\b/g, String(extra.fixLen ?? 0));
    expr = expr.replace(/\bfix\.sum\b/g, String(extra.fixSum ?? 0));

    try {
      const result = new Function(`return (${expr});`)();
      const n = Number(result);
      return Number.isFinite(n) ? n : 0;
    } catch (err) {
      console.warn("DynamicValueList formula error:", { formula, expr, err });
      return 0;
    }
  }

  function finalizeConfig(nextBase, options = {}) {
    const { changedKey = null, dimsOverride = dims } = options;

    let next = { ...nextBase };

    // 1) először a normál dependent / makrós értékek frissüljenek
    next = recomputeDependentValues(next, dimsOverride);

    // 2) repeater sorok száma igazodjon az új állapothoz
    next = syncRepeaterRows(next, changedKey, dimsOverride);

    // 3) dependentek újra, mert a repeater is hozhatott új értékeket
    next = recomputeDependentValues(next, dimsOverride);

    // 4) dynamic sorok száma + értéke igazodjon
    next = syncDynamicValueListRows(next, changedKey, dimsOverride);

    return next;
  }

  function recomputeDynamicValueListValues(next, settingId, raw, dimsObj) {
    const dyn = parseDynamicValueListConfig(raw);
    if (!dyn?.countId) return next;

    const N = getDynamicRowCount(raw, next);

    let fixLen = 0;
    let fixSum = 0;

    for (let i = 1; i <= N; i++) {
      const valueKey = getDynamicRowValueKey(settingId, i);
      const fixedKey = getDynamicRowFixedKey(settingId, i);

      if (next[fixedKey] === true) {
        fixLen++;
        fixSum += Number(next[valueKey] ?? 0) || 0;
      }
    }

    const autoCount = N - fixLen;
    if (autoCount <= 0) return next;

    const computedValue = evaluateDynamicFormula(dyn.formula, next, dimsObj, {
      allLen: N,
      fixLen,
      fixSum
    });

    for (let i = 1; i <= N; i++) {
      const valueKey = getDynamicRowValueKey(settingId, i);
      const fixedKey = getDynamicRowFixedKey(settingId, i);

      if (next[fixedKey] !== true) {
        next[valueKey] = computedValue;
      }
    }

    return next;
  }
  const getDefs = async (sid) => {
    if (defsCache.has(sid)) return defsCache.get(sid);
    let arr = await scriptItemApi.getAllScriptItemsForScriptApi(sid);
    // ha az API nem ad azonnali tömböt, olvassunk vissza a store-ból
    if (!Array.isArray(arr) || arr.length === 0) {
      try {
        await dispatch(getScriptItemsByScript(sid));
      } catch {}
      const state = store.getState();
      const all = state?.scriptItems || [];
      arr = all.filter((d) => String(d.scriptId) === String(sid));
    }
    const defs = Array.isArray(arr) ? arr : [];
    setDefsCache((prev) => {
      const m = new Map(prev);
      m.set(sid, defs);
      return m;
    });
    return defs;
  };

  // helper: vágd le a top-level |hint-et és a külső idézőjeleket
  function stripRef(raw) {
    let s = String(raw || "").trim();
    if (
      (s.startsWith('"') && s.endsWith('"')) ||
      (s.startsWith("'") && s.endsWith("'"))
    ) {
      s = s.slice(1, -1);
    }
    // csak top-level '|'-ig vágjunk (ne belső zárójelben)
    let depth = 0;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === "(") depth++;
      else if (ch === ")") depth--;
      else if (ch === "|" && depth === 0) return s.slice(0, i).trim();
    }
    return s;
  }

  const collectRefIdsFromDefs = (defs, cfg) => {
    const ids = new Set();

    for (const it of defs) {
      const raw =
        it.refScriptExpr ??
        it.refScriptId ??
        it.refScript ??
        it.ref_script_expr ??
        it.ref_script_id;
      if (!raw) continue;

      let s = stripRef(raw); // levágja a top-level hintet és az idézőket

      // 1) tiszta szám
      if (/^\d+$/.test(s)) {
        ids.add(Number(s));
        continue;
      }

      // 2) tiszta makró: (*71*) vagy (*71@2*)
      const m = s.match(/^\(\*(\d+)(?:@(\d+))?\*\)$/);
      if (m) {
        const baseId = m[1];
        const sec = m[2] ? Number(m[2]) : null;
        if (sec) {
          const v = Number(cfg[`${baseId}@${sec}`]);
          if (Number.isFinite(v)) ids.add(v);
        } else {
          Object.keys(cfg)
            .filter((k) => k.startsWith(`${baseId}@`))
            .forEach((k) => {
              const v = Number(cfg[k]);
              if (Number.isFinite(v)) ids.add(v);
            });
        }
        continue;
      }

      // 3) általános kifejezés – próbáljuk kiértékelni több szekcióval
      try {
        const macro = s.match(/\(\*(\d+)\*\)/);
        const base = macro ? macro[1] : null;
        const secs = base
          ? Object.keys(cfg)
              .filter((k) => k.startsWith(`${base}@`))
              .map((k) => Number(k.split("@")[1]))
          : [null];

        const trySecs = secs.length ? secs : [null];

        for (const sec of trySecs) {
          const local = { ...cfg };
          if (
            base &&
            sec != null &&
            local[base] == null &&
            local[`${base}@${sec}`] != null
          ) {
            local[base] = local[`${base}@${sec}`];
          }
          const expr = s
            .replace(/\(\*(\d+(?:@\d+)?)\*\)/g, (_, k) =>
              JSON.stringify(local[k] ?? 0)
            )
            .replace(/\b(width|height|depth)\b/g, (__, k) =>
              String(local[k] ?? 0)
            );
          const v = new Function(`return (${expr});`)();
          const n = Number(v);
          if (Number.isFinite(n)) ids.add(n);
        }
      } catch {
        (s.match(/\b\d+\b/g) || []).forEach((n) => ids.add(Number(n)));
      }
    }

    return ids;
  };

  const refHintsByAnchor = useMemo(() => {
    const map = {};
    const defs = defsCache.get(selectedScript?.scriptId) || [];
    defs.forEach((it) => {
      const raw =
        it.refScriptExpr ??
        it.refScriptId ??
        it.refScript ??
        it.ref_script_expr ??
        it.ref_script_id;
      if (!raw) return;
      const { split, group, macroId } = parseRefMods(raw);
      if (!macroId) return;
      map[macroId] = { split, group };
    });
    return map;
  }, [defsCache, selectedScript]);

  const buildAllDefsForConfig = async (rootScriptId, cfg) => {
    const seen = new Set();
    const queue = [Number(rootScriptId)];
    let bundle = [];

    while (queue.length) {
      const sid = queue.shift();
      if (seen.has(sid)) continue;
      seen.add(sid);

      const defs = await getDefs(sid); // cache-elt betöltés
      bundle.push(...defs);

      const refIds = collectRefIdsFromDefs(defs, cfg);
      refIds.forEach((id) => {
        if (!seen.has(id)) queue.push(id);
      });
    }
    return bundle;
  };

  // --- EDDIGI settingsMeta helyett:
  function syncDynamicValueListRows(next, changedKey = null, dimsObj = dims) {
    (settingsMeta || []).forEach((meta) => {
      if (!meta?.isDynamicValueList) return;

      const id = String(meta.settingId);

      const raw =
        next[id] ??
        getRawSettingDefinition(selectedScript, id) ??
        meta.defaultValue ??
        "";

      const dyn = parseDynamicValueListConfig(raw);
      if (!dyn?.countId) return;

      if (changedKey && String(dyn.countId) !== String(changedKey)) {
        // changedKey esetén is újraszámolunk majd a finalizeConfig miatt,
        // de ezt a szűrést csak a sorok számához tartjuk meg.
      }

      const countKey = dyn.countSection
        ? `${dyn.countId}@${dyn.countSection}`
        : dyn.countId;

      const N = parseInt(next[countKey] ?? 0, 10) || 0;

      for (let i = 1; i <= N; i++) {
        const valueKey = getDynamicRowValueKey(id, i);
        const fixedKey = getDynamicRowFixedKey(id, i);

        if (next[fixedKey] == null) next[fixedKey] = false;
        if (next[valueKey] == null || next[valueKey] === "") next[valueKey] = 0;
      }

      Object.keys(next)
        .filter((k) => k.startsWith(`${id}@`) && !k.includes("__child__"))
        .forEach((k) => {
          const m = k.match(new RegExp(`^${id}@(\\d+)(?:__fixed)?$`));
          const ix = m ? parseInt(m[1], 10) : NaN;
          if (Number.isFinite(ix) && ix > N) delete next[k];
        });

      recomputeDynamicValueListValues(next, id, raw, dimsObj);
    });

    return next;
  }
  const getSettingById = (list, id) =>
    (list || []).find((s) => String(s.settingId) === String(id)) || null;

  const settingsMeta = useMemo(() => {
    return (allSettings || []).map((s) => {
      const valuePairs = parseValueListPairs(s.valueList);
      const raw = String(s.value || "").trim();

      let repeatSource = null,
        repeatCountFrom = null;

      if (raw.startsWith("repeat:")) {
        const parts = raw
          .slice("repeat:".length)
          .split(";")
          .map((seg) => seg.split("=").map((x) => x.trim()))
          .filter(([k, v]) => k && v);
        const map = Object.fromEntries(parts);
        if (map.source) repeatSource = Number(map.source);
        if (map.count) repeatCountFrom = map.count;
      }

      const settingType = s.settingType ?? s.setting_type ?? "";

      return {
        ...s,
        settingType,
        valuePairs,
        repeatSource,
        repeatCountFrom,
        isDynamicValueList: settingType === "DynamicValueList",
        defaultValue: s.value
      };
    });
  }, [allSettings]);

  useEffect(() => {
    if (!initialScriptId) return;
    const init = async () => {
      const script = scripts.find((s) => s.scriptId === initialScriptId);
      if (script) setSelectedScript(script);

      await scriptItemApi.getAllScriptItemsForScriptApi(initialScriptId);
      await dispatch(getScriptItemsByScript(initialScriptId));

      if (initialGeneratedItems.length > 0) {
        setGeneratedItems(initialGeneratedItems);
      } else {
        // itt a selectScript már a DEFINÍCIÓKKAL fog futni
        selectScript(script);
      }
    };
    if (initialConfig) {
      setConfig(initialConfig);
    }
    if (
      initialDims &&
      typeof initialDims.width === "number" &&
      typeof initialDims.height === "number" &&
      typeof initialDims.depth === "number"
    ) {
      setDims(initialDims);
    }
    init();
  }, [initialScriptId, initialGeneratedItems, scripts, dispatch]);

  const handleDimBlur = (dim) => (e) => {
    const v = e.target.value;
    setDims((prev) => ({ ...prev, [dim]: v === "" ? "" : Number(v) }));
  };
  // available rooms (excluding "All")
  const rooms = useMemo(() => {
    const result = Array.from(
      new Set(scripts.map((s) => s.room).filter((r) => r && r !== "All"))
    );
    console.log("rooms: ", result);
    return result;
  }, [scripts]);

  const repeaterIndices = (settingId) => {
    const prefix = `${settingId}@`;
    return Object.keys(config)
      .filter((k) => k.startsWith(prefix))
      .map((k) => parseInt(k.slice(prefix.length), 10))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
  };

  const setRepeaterValue = (settingId, index, value) => {
    setConfig((prev) => {
      const key = `${settingId}@${index}`;
      const base = { ...prev, [key]: value };
      return finalizeConfig(base, { changedKey: key });
    });
  };
  const dimLabels = {
    width: t("width"),
    height: t("height"),
    depth: t("depth")
  };
  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const m = String(draggableId).match(/^item-(-?\d+)$/);
    const idNum = m ? Number(m[1]) : Number(draggableId.replace(/^item-/, ""));
    if (Number.isNaN(idNum)) return; // biztonság
    if (!Number.isFinite(idNum)) return;

    // keresd az indexet itemId vagy gen_ alapján
    const idx = generatedItems.findIndex(
      (it, i) =>
        (it.itemId != null && Number(it.itemId) === idNum) ||
        (it.itemId?.startsWith("gen_") &&
          Number(it.itemId.slice(4)) === idNum) ||
        i === idNum
    );
    if (idx < 0) return;
    const newColorId =
      destination.droppableId === "no-color"
        ? null
        : Number(destination.droppableId);
    if (newColorId === generatedItems[idx].colorId) return; // nincs változás
    setGeneratedItems((old) => {
      const copy = [...old];
      copy[idx] = {
        ...copy[idx],
        colorId: newColorId,
        color: null,
        __userColored: true
      };
      generatedItemsRef.current = copy;
      return copy;
    });
  };

  const handleItemUpdate = (payload) => {
    setGeneratedItems((prev) => {
      const copy = [...prev];

      const apply = (u) => {
        const idx = resolveIndex(u, copy);
        if (idx < 0) return;

        copy[idx] = {
          ...copy[idx],
          position: u.position,
          rotation: u.rotation,
          __userMoved: true
        };
      };

      if (payload?.type === "batch") {
        payload.updates.forEach(apply);
      } else {
        apply(payload);
      }

      generatedItemsRef.current = copy;
      return copy;
    });
  };

  function resolveIndex(payload, items) {
    if (!payload.uid) return -1;
    return items.findIndex((it) => it.uid === payload.uid);
  }

  // add new default item
  const handleAddNew = () => {
    const defaultItem = {
      size: "[1,1,18]",
      sizeArr: [1, 1, 1],
      qty: 1,
      kant: "[-,0,0]",
      colorId: null,
      rotable: true,
      details: "",
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      material: "",
      name: "Új elem"
    };
    setGeneratedItems((old) => [...old, defaultItem]);
  };

  // when a script is chosen
  async function selectScript(script) {
    setSelectedScript(script);
    const cfg = parseSetting(script.setting);
    const allowedIdsForThisScript = (script.setting || "")
      .split(",")
      .map((p) => p.split(":")[0].trim())
      .filter(Boolean);

    const cfgWithDefaults = allSettings.reduce(
      (acc, s) => {
        if (!allowedIdsForThisScript.includes(String(s.settingId))) return acc;
        if (s.valueList?.length && acc[s.settingId] == null) {
          acc[s.settingId] = s.valueList[0];
        }
        return acc;
      },
      { ...cfg }
    );
    allowedIdsForThisScript.forEach((id) => {
      const meta = getSettingById(settingsMeta, id);
      if (!meta?.isDynamicValueList) return;

      const raw = cfgWithDefaults[id] ?? meta.defaultValue ?? "";
      const dyn = parseDynamicValueListConfig(raw);
      if (!dyn?.countId) return;

      const countKey = dyn.countSection
        ? `${dyn.countId}@${dyn.countSection}`
        : dyn.countId;

      const N = parseInt(cfgWithDefaults[countKey] ?? 0, 10) || 0;
      const initialValue = evaluateDynamicFormula(
        dyn.formula,
        cfgWithDefaults,
        {
          width: cfgWithDefaults.width,
          height: cfgWithDefaults.height,
          depth: cfgWithDefaults.depth
        }
      );

      for (let i = 1; i <= N; i++) {
        const valueKey = getDynamicRowValueKey(id, i);
        const fixedKey = getDynamicRowFixedKey(id, i);

        if (cfgWithDefaults[fixedKey] == null)
          cfgWithDefaults[fixedKey] = false;
        if (cfgWithDefaults[valueKey] == null) cfgWithDefaults[valueKey] = 0;
      }

      recomputeDynamicValueListValues(cfgWithDefaults, id, raw, {
        width: cfgWithDefaults.width,
        height: cfgWithDefaults.height,
        depth: cfgWithDefaults.depth
      });

      Object.keys(cfgWithDefaults)
        .filter((k) => {
          const m = k.match(new RegExp(`^${id}@(\\d+)(?:__fixed)?$`));
          if (!m) return false;
          const ix = parseInt(m[1], 10);
          return Number.isFinite(ix) && ix > N;
        })
        .forEach((k) => delete cfgWithDefaults[k]);
    });
    setDefaultConfig(JSON.stringify(cfgWithDefaults));
    //setConfig(cfgWithDefaults);
    setDims({
      width: cfgWithDefaults.width ?? "",
      height: cfgWithDefaults.height ?? "",
      depth: cfgWithDefaults.depth ?? ""
    });

    try {
      await scriptItemApi.getAllScriptItemsForScriptApi(script.scriptId);
      await dispatch(getScriptItemsByScript(script.scriptId));
    } catch (error) {
      console.error("Error fetching script items:", error);
    }

    allowedIdsForThisScript.forEach((id) => {
      const meta = getSettingById(settingsMeta, id);
      const repHint = parseRepeatConfig(cfgWithDefaults[id]); // <-- script.setting-ből is jöhet

      if (
        (meta?.repeatSource != null || repHint) &&
        cfgWithDefaults[id] == null
      ) {
        cfgWithDefaults[id] = ""; // anchor, hogy megjelenjen a mező
      } else if (meta?.valuePairs?.length && cfgWithDefaults[id] == null) {
        const def = meta.defaultValue || meta.valuePairs[0]?.value || "";
        cfgWithDefaults[id] = def;
      }
    });

    // ismétlődők sorainak felvétele a count alapján
    allowedIdsForThisScript.forEach((id) => {
      const meta = getSettingById(settingsMeta, id);
      const hint = parseRepeatConfig(cfgWithDefaults[id]); // <-- script.setting-ből jövő repeat:...

      const rep = {
        source: meta?.repeatSource ?? hint?.source ?? null,
        countId: meta?.repeatCountFrom ?? hint?.countId ?? null
      };
      if (!rep.source) return;

      const sourceMeta = getSettingById(settingsMeta, rep.source);
      const options = sourceMeta?.valuePairs?.length
        ? sourceMeta.valuePairs
        : (sourceMeta?.valueList || []).map((v) => ({ label: v, value: v }));

      const N = parseInt(cfgWithDefaults[rep.countId] ?? 0, 10) || 0;
      const defVal = sourceMeta?.defaultValue || options[0]?.value || "";

      for (let i = 1; i <= N; i++) {
        const key = `${id}@${i}`;
        if (cfgWithDefaults[key] == null) cfgWithDefaults[key] = defVal;
      }
      // fölös sorok törlése
      Object.keys(cfgWithDefaults)
        .filter((k) => k.startsWith(`${id}@`))
        .map((k) => parseInt(k.split("@")[1], 10))
        .filter(Number.isFinite)
        .forEach((ix) => {
          if (ix > N) delete cfgWithDefaults[`${id}@${ix}`];
        });
    });

    const finalConfig = finalizeConfig(cfgWithDefaults, {
      dimsOverride: {
        width: cfgWithDefaults.width,
        height: cfgWithDefaults.height,
        depth: cfgWithDefaults.depth
      }
    });
    setConfig(finalConfig);

    // ez a fontos sor
    setDefaultConfig(JSON.stringify(finalConfig));
    // egyesített defs
    const allDefs = await buildAllDefsForConfig(
      script.scriptId,
      cfgWithDefaults
    );
    // és csak ezután generáljuk az itemeket ezzel a végleges cfg-vel
    const runtimeConfig = buildProcessScriptConfig(finalConfig);

    const initialItems = processScript(
      runtimeConfig,
      {
        width: finalConfig.width,
        height: finalConfig.height,
        depth: finalConfig.depth
      },
      script.scriptId,
      allDefs
    );

    setGeneratedItems(
      initialItems.map((it, i) => ({
        ...it,
        itemId: nextDraftId(), // ← STABIL
        uid: createUid(),

        colorId:
          it.material?.toUpperCase() === "PFL" ? 0 : (it.colorId ?? null),
        __userMoved: false
      }))
    );
    if (script.__isSaved) {
      setDefaultConfig(JSON.stringify(script.savedConfig || {}));
    }
    setHiddenItems(new Set());
  }
  const isConfigModified = useMemo(() => {
    if (!defaultConfig) return false;

    try {
      const clean = { ...config };
      delete clean.width;
      delete clean.height;
      delete clean.depth;

      const cleanDefault = JSON.parse(defaultConfig);

      delete cleanDefault.width;
      delete cleanDefault.height;
      delete cleanDefault.depth;

      const normalize = (obj) =>
        JSON.stringify(
          Object.keys(obj)
            .sort()
            .reduce((acc, k) => {
              acc[k] = obj[k];
              return acc;
            }, {})
        );

      return normalize(clean) !== normalize(cleanDefault);
    } catch {
      return false;
    }
  }, [config, defaultConfig]);
  const scriptSavesForCurrentScript = useMemo(() => {
    if (!selectedScript) return [];
    return scriptSaves.filter((s) => s.scriptId === selectedScript.scriptId);
  }, [scriptSaves, selectedScript]);
  async function handleSaveConfig() {
    const name = prompt(t("saveConfigPrompt"));
    if (!name) return;

    try {
      await dispatch(
        scriptSaveApi.createScriptSaveApi({
          scriptId: selectedScript.scriptId,
          name: name,
          setting: JSON.stringify(config)
        })
      );
    } catch (err) {
      console.error("Save config error:", err);
    }
  }
  async function loadSavedConfig(save) {
    try {
      const cfg = JSON.parse(save.setting);

      const width = cfg.width ?? dims.width;
      const height = cfg.height ?? dims.height;
      const depth = cfg.depth ?? dims.depth;

      const nextDims = { width, height, depth };
      setDims(nextDims);
      setConfig(finalizeConfig(cfg, { dimsOverride: nextDims }));

      const allDefs = await buildAllDefsForConfig(selectedScript.scriptId, cfg);

      const runtimeConfig = buildProcessScriptConfig(cfg);

      const items = processScript(
        runtimeConfig,
        { width, height, depth },
        selectedScript.scriptId,
        allDefs
      );

      setGeneratedItems(
        items.map((it, i) => ({
          ...it,
          itemId: nextDraftId(),
          uid: createUid(),
          colorId:
            it.material?.toUpperCase() === "PFL" ? 0 : (it.colorId ?? null),
          __userMoved: false
        }))
      );
    } catch (e) {
      console.error("Invalid saved config", e);
    }
  }
  function handleConfigChange(e) {
    const { name, value } = e.target;

    setConfig((prev) => {
      const base = { ...prev, [name]: value };
      return finalizeConfig(base, { changedKey: name });
    });
  }

  // go back to script selection
  function handleBack() {
    setSelectedScript(null);
    setConfig({});
    dispatch(clearSelectedScriptItems());
    setGeneratedItems([]);
    setHiddenItems(new Set());
  }

  // ScriptCaller.jsx

  async function handleGenerate() {
    if (!selectedScript) return;
    const { width, height, depth } = dims;
    const prevById = new Map(
      generatedItems.map((it, i) => [String(it.itemId ?? `gen_${i}`), it])
    );
    const updatedConfig = { ...config, height, width, depth };
    setConfig(updatedConfig);

    const runtimeConfig = buildProcessScriptConfig(updatedConfig);

    const allDefs = await buildAllDefsForConfig(
      selectedScript.scriptId,
      updatedConfig
    );

    const allItems = processScript(
      runtimeConfig,
      { height, width, depth },
      selectedScript.scriptId,
      allDefs
    );

    console.log("before processscript: ", generatedItems);
    console.log("after processscript: ", allItems);

    // 👇 EZ ÚJ: részletes log
    console.log("🧾 processScript – input & output:", {
      input: {
        config: updatedConfig,
        dims: { width, height, depth },
        scriptId: selectedScript.scriptId
      },
      output: allItems
    });

    function toSizeArray(val) {
      if (Array.isArray(val)) return val.map(Number);
      if (typeof val === "string") {
        try {
          const arr = JSON.parse(val);
          if (Array.isArray(arr)) return arr.map(Number);
        } catch {}
        // fallback: regex
        return [...String(val).matchAll(/-?\d+(\.\d+)?/g)].map((m) =>
          Number(m[0])
        );
      }
      return [0, 0, 0];
    }

    setGeneratedItems((prev) => {
      const next = allItems.map((it, i) => {
        const key = String(it.itemId ?? `gen_${i}`);
        const old = prevById.get(key);
        if (!old) {
          console.warn("❌ LOST OLD ITEM", {
            newItem: it,
            lookupKeys: [String(it.itemId), `gen_${i}`],
            prevKeys: [...prevById.keys()]
          });
        }
        const userMoved = old?.__userMoved === true;
        const userColored = old?.__userColored === true;
        return {
          ...it,
          uid: old?.uid ?? createUid(),
          size: toSizeArray(it.size),
          colorId: userColored
            ? old.colorId
            : it.material?.toUpperCase() === "PFL"
              ? 0
              : (it.colorId ?? null),
          position: userMoved ? old.position : it.position,
          rotation: userMoved ? old.rotation : it.rotation,
          __userMoved: userMoved,
          __isGenerated: true,
          itemId: old?.itemId ?? nextDraftId()
        };
      });
      generatedItemsRef.current = next;
      return next;
    });

    setHiddenItems(new Set());
  }
  async function handleSave() {
    if (!generatedItems.length) return;

    try {
      const selectedWorkId =
        typeof selectedWorkFromStore === "number"
          ? selectedWorkFromStore
          : selectedWorkFromStore?.workId;

      if (!selectedWorkId) {
        console.error("Nincs workId a createObject payloadban");
        return;
      }
      console.group("💾 ScriptCaller SAVE – runtime state");
      console.log(
        generatedItems.map((it, i) => ({
          i,
          itemId: it.itemId,
          position: it.position,
          rotation: it.rotation,
          size: it.size,
          __userMoved: it.__userMoved
        }))
      );
      console.groupEnd();

      const tmpObjectId = initialObject?.objectId ?? nextDraftId();
      const selectedClientId =
        typeof selectedClientFromStore === "number"
          ? selectedClientFromStore
          : selectedClientFromStore?.clientId;
      // 👉 DRAFT OBJECT – nincs backend
      dispatch(
        addDraftObject({
          objectId: tmpObjectId,
          __isDraft: true,
          name: selectedScript.name,
          usedScript: { scriptId: selectedScript.scriptId },
          work: { workId: selectedWorkId },
          client: selectedClientId ? { clientId: selectedClientId } : undefined,
          setting: JSON.stringify(config),
          usedColors: JSON.stringify(palette.map((c) => c.colorId))
        })
      );

      (generatedItemsRef.current.length
        ? generatedItemsRef.current
        : generatedItems
      ).forEach((item, i) => {
        const payload = {
          ...item,
          position: normalizeVec(item.position),
          rotation: normalizeVec(item.rotation),
          size: normalizeVec(item.size),
          itemId: nextDraftId(),
          __isDraft: true,
          object: { objectId: tmpObjectId },
          color: item.colorId === 0 ? { colorId: 0 } : (item.color ?? null),
          work: { workId: selectedWorkId }
        };
        console.log("📦 addDraftCreatedItem payload:", {
          index: i,
          payload
        });

        dispatch(addDraftCreatedItem(payload));
      });

      onSave?.(tmpObjectId);
    } catch (err) {
      console.error("Hiba mentéskor:", err);
    }
  }
  const handleItemColorChange = (index, color) => {
    handleItemChange(index, { colorId: color.colorId });
  };

  // általános item változtatás pl. mennyiség vagy size
  const handleItemChange = (index, updatedFields) => {
    const updated = {
      ...generatedItems[index],
      ...updatedFields,
      color:
        updatedFields.colorId !== undefined
          ? null
          : generatedItems[index].color,

      __userResized: true,
      __userColored:
        "colorId" in updatedFields ? true : generatedItems[index].__userColored
    };
    setGeneratedItems((prev) => {
      const arr = [...prev];
      arr[index] = updated;
      return arr;
    });
  };
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);

  useEffect(() => {
    // ha már az ObjectViewer-t nézzük, ne inicializáljuk újra a canvas-t
    if (objectToView) return;

    if (!canvasRef.current) return;
    const engine = new Engine(canvasRef.current, true);
    const scene = new Scene(engine);
    scene.clearColor = new Color4(1, 1, 1, 1);
    sceneRef.current = scene;

    const camera = new ArcRotateCamera(
      "camera",
      Math.PI / 2,
      Math.PI / 4,
      10,
      Vector3.Zero(),
      scene
    );
    camera.attachControl(canvasRef.current, true);
    camera.wheelPrecision = 50;
    new HemisphericLight("light", new Vector3(0, 1, 0), scene);

    engine.runRenderLoop(() => scene.render());
    return () => engine.dispose();
  }, [objectToView]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    // régi box-ok eltávolítása
    scene.meshes
      .filter((m) => m.name.startsWith("box_"))
      .forEach((m) => m.dispose());

    // újraszámoljuk a skálát a dims alapján
    const wNum = dims.width || 0;
    const hNum = dims.height || 0;
    const dNum = dims.depth || 0;
    const maxDim = Math.max(wNum, hNum, dNum) || 1;
    const globalScale = 1 / maxDim;
    generatedItems.forEach((itm, idx) => {
      const [w, h, d] = itm.size;
      const [[px, py, pz]] = parseBracketList(itm.position, 1);
      const [rx, ry, rz] = itm.rotation;
      const sw = w * globalScale,
        sh = h * globalScale,
        sd = d * globalScale;
      const x = px * globalScale,
        y = py * globalScale,
        z = pz * globalScale;
      const box = MeshBuilder.CreateBox(
        `box_${idx}`,
        { width: sw, height: sh, depth: sd },
        scene
      );
      box.position = new Vector3(x + sw / 2, y + sh / 2, z + sd / 2);
      box.rotation = new Vector3(
        (rx * Math.PI) / 2,
        (ry * Math.PI) / 2,
        (rz * Math.PI) / 2
      );
      const mat = new StandardMaterial(`mat_${idx}`, scene);
      mat.diffuseColor = new Color3(0.3, 0.3, 0.3);
      box.material = mat;
    });
  }, [generatedItems, dims]);
  function getRawSettingDefinition(script, settingId) {
    if (!script?.setting) return null;

    const entry = script.setting
      .split(",")
      .map((p) => p.trim())
      .find((p) => p.startsWith(`${settingId}:`));

    if (!entry) return null;
    return entry.slice(entry.indexOf(":") + 1).trim();
  }
  function Header() {
    return (
      <Container fluid className="border-bottom script-caller-header">
        <Row className="align-items-center g-2 script-caller-header-row">
          <Col xs={12} xl="auto" className="script-caller-header-title-col">
            <h3 className="script-caller-header-title">
              {selectedScript ? t(selectedScript.name) : t("selectScript")}
            </h3>
          </Col>

          {!selectedScript && (
            <Col className="script-caller-room-select-col">
              <Form.Group className="script-caller-room-select-group d-flex align-items-center gap-2">
                <Form.Label className="mb-0 text-nowrap">
                  {t("selectRoom")}
                </Form.Label>

                <Form.Select
                  value={selectedRoom}
                  onChange={(e) => setSelectedRoom(e.target.value)}
                >
                  <option value="">{t("selectRoomPlaceholder")}</option>
                  {rooms.map((room) => (
                    <option key={room} value={room}>
                      {t(room)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          {selectedScript && (
            <>
              <Col xs={12} xl className="script-caller-header-dimensions-col">
                <div className="script-caller-header-dimensions">
                  {["width", "height", "depth"].map((dim) => (
                    <Form.Group
                      key={dim}
                      className="script-caller__dimension-group"
                    >
                      <Form.Label className="mb-1 text-capitalize">
                        {dimLabels[dim]}
                      </Form.Label>

                      <Form.Control
                        type="number"
                        size="sm"
                        defaultValue={dims[dim]}
                        onBlur={handleDimBlur(dim)}
                      />
                    </Form.Group>
                  ))}
                </div>
              </Col>

              <Col
                xs={12}
                xl="auto"
                className="script-caller-header-actions-col"
              >
                <div className="script-caller-header-actions">
                  <Button
                    variant="outline-secondary"
                    onClick={handleBack}
                    size="sm"
                  >
                    {t("back")}
                  </Button>

                  <Button variant="primary" onClick={handleGenerate} size="sm">
                    {t("generateBtn")}
                  </Button>

                  <Button variant="success" onClick={handleSave} size="sm">
                    {t("saveFurniture")}
                  </Button>
                </div>
              </Col>
            </>
          )}
        </Row>
      </Container>
    );
  }

  return (
    <div className="script-caller">
      <Header />
      {/*      <style>{`
        @keyframes marqueeX {
          from { transform: translateX(0); }
          to   { transform: translateX(calc(-1 * var(--moveDistance, 0px))); }
        }
        `}</style>*/}

      {!selectedScript ? (
        <Container
          className="mt-4 script-caller-script-selector-container"
          fluid
        >
          {selectedRoom && (
            <div className="script-caller-script-grid">
              {scriptsForRoom
                .filter((s) => s.room === selectedRoom || s.room === "All")
                .map((s) => (
                  <div
                    key={s.saveId ?? s.scriptId}
                    className={`me-3 p-2 border script-caller-script-card ${
                      hoveredScript === (s.saveId ?? s.scriptId)
                        ? "script-caller-script-card-hovered"
                        : ""
                    }`}
                    onClick={() => selectScript(s)}
                    onMouseEnter={handleCardEnter(s.saveId ?? s.scriptId)}
                    onMouseLeave={handleCardLeave}
                  >
                    {s.__isSaved && (
                      <div className="script-caller-saved-star">⭐</div>
                    )}
                    {s.imageData && (
                      <img
                        src={`data:image/jpeg;base64,${s.imageData}`}
                        alt={t(s.name)}
                        className="script-caller-script-image"
                      />
                    )}

                    {/* Név sáv */}
                    <div className="script-caller-script-name-wrapper">
                      {/* Alap: ellipsis */}
                      <div
                        data-script-name
                        className={`script-caller-script-name ${
                          hoveredScript === (s.saveId ?? s.scriptId) &&
                          hoverMeta.distance > 0
                            ? "script-caller-script-name-hidden"
                            : ""
                        }`}
                        title={s.saveName || t(s.name)}
                      >
                        {s.saveName || t(s.name)}
                      </div>

                      {/* Hover overlay: a FIXED nézetablak marad, a BELSŐ TRACK mozog */}
                      {hoveredScript === (s.saveId ?? s.scriptId) &&
                        hoverMeta.distance > 0 && (
                          <div className="script-caller-marquee-window">
                            <div
                              className="script-caller-marquee-track"
                              style={{
                                "--moveDistance": `${hoverMeta.distance}px`,
                                "--marquee-duration": `${hoverMeta.duration}s`,
                                "--marquee-gap": `${hoverMeta.gap || 60}px`
                              }}
                            >
                              <span>{t(s.name)}</span>
                              {/* kis rés a két példány között, hogy legyen „szusszanás” */}
                              <span className="script-caller-marquee-gap">
                                {t(s.name)}
                              </span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Container>
      ) : (
        <Container fluid className="script-caller-main-container">
          <Row className="g-3 script-caller-main-row">
            <Col
              xs={12}
              lg={3}
              className="border-end d-flex flex-column script-caller-settings-panel"
            >
              {/* Sticky fejléc */}
              <div className="script-caller-settings-toolbar">
                <h5 className="script-caller-settings-title">
                  {t("settings")}
                </h5>

                <div className="script-caller-settings-toolbar-actions">
                  {scriptSavesForCurrentScript.length > 0 && (
                    <Form.Select
                      size="sm"
                      className="script-caller-settings-preset-select"
                      onChange={(e) => {
                        if (e.target.value === "original") {
                          const cfg = JSON.parse(defaultConfig);

                          loadSavedConfig({
                            setting: JSON.stringify(cfg)
                          });

                          return;
                        }

                        const save = scriptSavesForCurrentScript.find(
                          (s) => s.saveId == e.target.value
                        );

                        if (save) loadSavedConfig(save);
                      }}
                    >
                      <option value="original">{t("originalSettings")}</option>
                      {scriptSavesForCurrentScript.map((s) => (
                        <option key={s.saveId} value={s.saveId}>
                          {t(s.name)}
                        </option>
                      ))}
                    </Form.Select>
                  )}

                  {isConfigModified && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={handleSaveConfig}
                      className="script-caller-settings-save-button"
                    >
                      {t("saveSettings")}
                    </Button>
                  )}
                </div>
              </div>
              <div className="script-caller-settings-scroll">
                <Form>
                  <Row className="g-2">
                    <div className="mb-3">
                      <h6 className="text-uppercase text-muted mb-2">
                        {t("generalSettings")}
                      </h6>

                      {allowedSettingIds.map((id) => {
                        const meta = getSettingById(settingsMeta, id);
                        if (!meta) return null;
                        const isRepeater = meta.repeatSource != null;
                        const isDynamicValueList = meta.isDynamicValueList;
                        const val = config[id] ?? "";
                        if (isRepeater) {
                          const rows = repeaterIndices(id);

                          return (
                            <React.Fragment key={`global-${id}`}>
                              {rows.length === 0 && (
                                <Col
                                  xs={12}
                                  className="d-flex align-items-center mb-2"
                                >
                                  <Form.Label className="me-2 mb-0 script-caller-full-width-label">
                                    {settingNameById[id] || `#${id}`}
                                  </Form.Label>
                                  <div className="text-muted small script-caller-max-width-10rem">
                                    {t("noItems")}
                                  </div>
                                </Col>
                              )}

                              {rows.map((sec) => {
                                const grpLabel =
                                  (refHintsByAnchor[id]?.group ?? "").trim() ||
                                  settingNameById[id] ||
                                  `#${id}`;

                                const sid = Number(config[`${id}@${sec}`]);
                                const scr =
                                  scripts.find(
                                    (s) => Number(s.scriptId) === sid
                                  ) || null;

                                const childSettingIds = scr?.setting
                                  ? scr.setting
                                      .split(",")
                                      .map((p) => p.split(":")[0].trim())
                                      .filter(Boolean)
                                  : [];

                                const cardKey = `${id}@${sec}`; // ← kártya azonosító
                                // (lásd még lejjebb a resolvedOptions-t)
                                const sourceMeta = getSettingById(
                                  settingsMeta,
                                  meta.repeatSource
                                );
                                const rawOptions = sourceMeta?.valuePairs
                                  ?.length
                                  ? sourceMeta.valuePairs
                                  : (sourceMeta?.valueList || []).map((v) => ({
                                      label: v,
                                      value: v
                                    }));
                                const resolvedOptions = rawOptions.map(
                                  (opt) => ({
                                    label: resolveText(
                                      opt.label,
                                      config,
                                      dims,
                                      t
                                    ),
                                    value: resolveText(
                                      opt.value,
                                      config,
                                      dims,
                                      t
                                    )
                                  })
                                );
                                const nextDefault =
                                  sourceMeta?.defaultValue ||
                                  resolvedOptions[0]?.value ||
                                  "";

                                return (
                                  <Col xs={12} key={`global-${cardKey}`}>
                                    <div className="border rounded p-2 mb-3 script-caller-repeater-card">
                                      {/* FEJLÉC + toggle */}
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 d-flex align-items-center text-decoration-none"
                                          onClick={() => toggleBox(cardKey)}
                                        >
                                          <IonIcon
                                            icon={
                                              collapsedBox[cardKey]
                                                ? chevronForward
                                                : chevronDown
                                            }
                                            className="me-1"
                                          />
                                          <span className="fw-semibold">
                                            {sec}. {grpLabel} {t("settingsOf")}
                                          </span>
                                        </button>
                                        {scr && (
                                          <small className="text-muted">
                                            {t(scr.name)}
                                          </small>
                                        )}
                                      </div>

                                      {/* TARTALOM: csak ha nincs összecsukva */}
                                      {!collapsedBox[cardKey] && (
                                        <>
                                          {/* ANCHOR SELECT */}
                                          <div className="d-flex align-items-center mb-2">
                                            <Form.Label className="mb-0 script-caller-full-width-label">
                                              {settingNameById[id] || `#${id}`}
                                            </Form.Label>
                                            <Form.Select
                                              size="sm"
                                              value={
                                                config[cardKey] ?? nextDefault
                                              }
                                              onChange={(e) =>
                                                setRepeaterValue(
                                                  id,
                                                  sec,
                                                  e.target.value
                                                )
                                              }
                                              className="script-caller-max-width-10rem"
                                            >
                                              {resolvedOptions.map((opt) => (
                                                <option
                                                  key={opt.value}
                                                  value={opt.value}
                                                >
                                                  {opt.label}
                                                </option>
                                              ))}
                                            </Form.Select>
                                          </div>

                                          {/* GYEREK BEÁLLÍTÁSOK INLINE */}
                                          {childSettingIds.map((sid2) => {
                                            // ... childSettingIds.map((sid2) => {
                                            const cmeta = getSettingById(
                                              settingsMeta,
                                              sid2
                                            );
                                            if (!cmeta) return null;

                                            const childKey = `__child__:${id}@${sec}:${sid2}`;
                                            const childVal =
                                              config[childKey] ??
                                              config[
                                                `__child__:${sid}:${sid2}`
                                              ] ??
                                              config[sid2] ??
                                              cmeta.defaultValue ??
                                              "";

                                            const setChildVal = (nextVal) =>
                                              setConfig((prev) => {
                                                const base = {
                                                  ...prev,
                                                  [childKey]: nextVal
                                                };
                                                return finalizeConfig(base, {
                                                  changedKey: childKey
                                                });
                                              });

                                            const baseOpts = cmeta.valuePairs
                                              ?.length
                                              ? cmeta.valuePairs
                                              : (cmeta.valueList || []).map(
                                                  (v) => ({
                                                    label: v,
                                                    value: v
                                                  })
                                                );

                                            const childOpts = baseOpts.map(
                                              (opt) => ({
                                                label: resolveText(
                                                  opt.label,
                                                  config,
                                                  dims,
                                                  t
                                                ),
                                                value: resolveText(
                                                  opt.value,
                                                  config,
                                                  dims,
                                                  t
                                                )
                                              })
                                            );

                                            return (
                                              <div
                                                key={childKey}
                                                className="d-flex align-items-center mb-2"
                                              >
                                                <Form.Label
                                                  className="me-2 mb-0"
                                                  style={{ width: "100%" }}
                                                >
                                                  {settingNameById[sid2] ||
                                                    `#${sid2}`}
                                                </Form.Label>

                                                {childOpts.length ? (
                                                  <Form.Select
                                                    size="sm"
                                                    style={{
                                                      maxWidth: "10rem"
                                                    }}
                                                    value={resolveText(
                                                      childVal,
                                                      config,
                                                      dims,
                                                      t
                                                    )}
                                                    onChange={(e) =>
                                                      setChildVal(
                                                        e.target.value
                                                      )
                                                    }
                                                  >
                                                    {childOpts.map((opt) => (
                                                      <option
                                                        key={opt.value}
                                                        value={opt.value}
                                                      >
                                                        {t(opt.label)}
                                                      </option>
                                                    ))}
                                                  </Form.Select>
                                                ) : (
                                                  <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    className="script-caller-max-width-10rem"
                                                    value={resolveText(
                                                      childVal,
                                                      config,
                                                      dims,
                                                      t
                                                    )}
                                                    onChange={(e) =>
                                                      setChildVal(
                                                        e.target.value
                                                      )
                                                    }
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </>
                                      )}
                                    </div>
                                  </Col>
                                );
                              })}
                            </React.Fragment>
                          );
                        }
                        if (isDynamicValueList) {
                          const raw =
                            getRawSettingDefinition(selectedScript, id) ??
                            config[id] ??
                            meta.defaultValue ??
                            "";

                          const dyn = parseDynamicValueListConfig(raw);
                          const countKey = dyn?.countSection
                            ? `${dyn.countId}@${dyn.countSection}`
                            : dyn?.countId;

                          const rowCount =
                            parseInt(config[countKey] ?? 0, 10) || 0;

                          return (
                            <Col xs={12} key={`dynamic-${id}`}>
                              <div className="mb-3">
                                <h6 className="text-uppercase text-muted mb-2">
                                  {settingNameById[id] || `#${id}`}
                                </h6>

                                <div className="script-caller-dynamic-list">
                                  {Array.from(
                                    { length: rowCount },
                                    (_, idx) => {
                                      const rowIndex = idx + 1;
                                      const rowKey = getDynamicRowValueKey(
                                        id,
                                        rowIndex
                                      );
                                      const fixedKey = getDynamicRowFixedKey(
                                        id,
                                        rowIndex
                                      );

                                      const rowVal = config[rowKey] ?? 0;
                                      const isFixed = config[fixedKey] === true;

                                      return (
                                        <div
                                          key={rowKey}
                                          className="d-flex align-items-center mb-2 gap-2"
                                        >
                                          <Form.Label className="mb-0 text-muted script-caller-dynamic-row-label">
                                            {rowIndex}. {t("line")}
                                          </Form.Label>

                                          <Button
                                            size="sm"
                                            variant={
                                              isFixed
                                                ? "warning"
                                                : "outline-secondary"
                                            }
                                            onClick={() =>
                                              toggleDynamicRowFixed(
                                                id,
                                                rowIndex
                                              )
                                            }
                                            className="script-caller-dynamic-button"
                                          >
                                            {isFixed ? "Fix" : "Auto"}
                                          </Button>

                                          <Form.Control
                                            type="number"
                                            size="sm"
                                            value={rowVal}
                                            disabled={!isFixed}
                                            onChange={(e) =>
                                              setDynamicRowManualValue(
                                                id,
                                                rowIndex,
                                                e.target.value
                                              )
                                            }
                                            className="script-caller-max-width-10rem"
                                          />
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            </Col>
                          );
                        }
                        // ---- nem ismétlődő mező globálisan ----
                        return (
                          <Col
                            xs={12}
                            className="d-flex align-items-center mb-2"
                            key={`global-${id}`}
                          >
                            <Form.Label
                              className="me-2 mb-0"
                              style={{ width: "100%" }}
                            >
                              {settingNameById[id] || `#${id}`}
                            </Form.Label>

                            {meta.valuePairs?.length ? (
                              <Form.Select
                                size="sm"
                                name={id}
                                value={val}
                                onChange={handleConfigChange}
                                style={{ maxWidth: "10rem" }}
                              >
                                {meta.valuePairs.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {t(opt.label)}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : meta.valueList?.length ? (
                              <Form.Select
                                size="sm"
                                name={id}
                                value={val}
                                onChange={handleConfigChange}
                                style={{ maxWidth: "10rem" }}
                              >
                                {meta.valueList.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type="text"
                                size="sm"
                                name={id}
                                value={val}
                                onChange={handleConfigChange}
                                style={{ maxWidth: "10rem" }}
                              />
                            )}
                          </Col>
                        );
                      })}
                    </div>
                  </Row>
                </Form>
              </div>
            </Col>

            <Col
              xs={12}
              lg={6}
              className="text-center script-caller-viewer-column"
            >
              {generatedItems.length > 0 ? (
                <div className="script-caller-object-viewer-wrapper">
                  <ObjectViewer
                    object={
                      objectToViewData || {
                        objectId: 0,
                        position: "[0,0,0]",
                        rotation: "[0,0,0]"
                      }
                    }
                    createdItems={generatedItems}
                    usedColors={palette}
                    onItemUpdate={handleItemUpdate}
                    hiddenItems={hiddenItems}
                  />
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="script-caller-preview-canvas"
                />
              )}
            </Col>

            <Col
              xs={12}
              lg={3}
              className="border-start d-flex flex-column script-caller-items-column"
            >
              <div className="script-caller-items-panel">
                <h5 className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  {t("createdItemsTitle")}
                  <Button size="sm" variant="secondary" onClick={handleAddNew}>
                    {t("addNewSize")}
                  </Button>
                </h5>

                <div className="script-caller-generated-items-scroll">
                  <GeneratedItemsList
                    calledFrom={1}
                    generatedItems={generatedItems}
                    palette={palette}
                    collapsedColors={collapsedColors}
                    toggleColor={toggleColor}
                    handleItemChange={handleItemChange}
                    onSwap={(colorId) => {
                      setSwapSourceColor(colorId);
                      openPaletteModal();
                    }}
                    handleItemColorChange={handleItemColorChange}
                    onDragEnd={onDragEnd}
                    onDelete={(idx) =>
                      setGeneratedItems((old) =>
                        old.filter((_, i) => i !== idx)
                      )
                    }
                    hiddenItems={hiddenItems}
                    onToggleVisibility={handleToggleVisibility}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
}
